import Axios from 'axios';
import * as MessageRequest from '@/models/messageRequest';
import * as MessageResponse from '@/models/messageResponse';
import { AttachmentFileRequest, AttachmentFile } from '@/models/attachmentFile';
import { ReturnTypes } from '@/enums/returnTypes';
import { PublicTypes } from '@/enums/publicTypes';
import { ReadTypes } from '@/enums/readTypes';
import { MessageTypes } from '@/enums/messageTypes';

export type Message = MessageResponse.MessageSearch;
export type MessageDetail = MessageResponse.MessageDetail;
export type MessageComment = MessageResponse.MessageComment;

export interface SearchCondition {
  messageType: MessageTypes;
  publicType: PublicTypes;
  readType: ReadTypes;
  tutorIdList: string[];
  dateFrom: Date | null;
  dateTo: Date | null;
  keyWord: string;
  perPage: number;
  page: number;
  sort: string;
}

interface State {
  loading: boolean;
  countAll: number;
  messageList: Message[];
  searchCondition: SearchCondition;
  selectedMessage: MessageDetail | null;
  selectedComment: MessageComment | null;
}
const mapState: State = {
  loading: false,
  countAll: 0,
  messageList: new Array<Message>(),
  searchCondition: {
    messageType: MessageTypes.All,
    publicType: PublicTypes.All,
    readType: ReadTypes.All,
    tutorIdList: [],
    dateFrom: null,
    dateTo: null,
    keyWord: '',
    perPage: 20,
    page: 1,
    sort: 'updateDate descending'
  },
  selectedMessage: null,
  selectedComment: null
};

const mapActions = {
  async searchMessages(
    { dispatch, commit }: any,
    searchCondition: SearchCondition
  ) {
    commit('setLoading', true);
    const data = await dispatch('getMessages', searchCondition).catch(
      (error: any) => {
        commit('setLoading', false);
        throw error;
      }
    );
    commit('searchMessages', data);
  },
  async getMessages({ commit }: any, searchCondition: SearchCondition) {
    const request: MessageRequest.MessageRequest = {
      messageId: undefined,
      sendDate: undefined,
      title: undefined,
      note: undefined,
      attachments: undefined,
      returnType: undefined,
      studentIdList: undefined,
      notificationIdList: undefined,
      messageType: searchCondition.messageType,
      publicType: searchCondition.publicType,
      readType: searchCondition.readType,
      tutorIdList: searchCondition.tutorIdList,
      dateFrom: searchCondition.dateFrom,
      dateTo: searchCondition.dateTo,
      keyWord: searchCondition.keyWord,
      perPage: searchCondition.perPage,
      page: searchCondition.page,
      sort: searchCondition.sort
    };
    const response = await Axios.post('/api/Message/SearchList', request).catch(
      (error) => {
        throw error;
      }
    );
    response.data.messageList.forEach((message: any) => {
      if (typeof message.sendDate === 'string') {
        message.sendDate = new Date(message.sendDate + 'Z');
      }
      if (typeof message.latestDate === 'string') {
        message.latestDate = new Date(message.latestDate + 'Z');
      }
      if (typeof message.updateDate === 'string') {
        message.updateDate = new Date(message.updateDate + 'Z');
      }
    });
    return response.data;
  },
  async selectMessage({ dispatch, commit }: any, messageId: string) {
    commit('setLoading', true);
    const data = await dispatch('getMessageDetails', messageId).catch(
      (error: any) => {
        commit('setLoading', false);
        throw error;
      }
    );
    commit('selectMessage', data);
  },
  async getMessageDetails({ commit }: any, messageId: string) {
    const request: MessageRequest.MessageRequest = {
      messageId,
      sendDate: undefined,
      title: undefined,
      note: undefined,
      attachments: undefined,
      returnType: undefined,
      studentIdList: undefined,
      notificationIdList: undefined,
      messageType: undefined,
      publicType: undefined,
      readType: undefined,
      tutorIdList: undefined,
      dateFrom: undefined,
      dateTo: undefined,
      keyWord: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined
    };
    const response = await Axios.post('/api/Message/Detail', request).catch(
      (error) => {
        throw error;
      }
    );
    if (typeof response.data.sendDate === 'string') {
      response.data.sendDate = new Date(response.data.sendDate + 'Z');
    }
    if (typeof response.data.latestDate === 'string') {
      response.data.latestDate = new Date(response.data.latestDate + 'Z');
    }
    if (typeof response.data.updateDate === 'string') {
      response.data.updateDate = new Date(response.data.updateDate + 'Z');
    }
    response.data.studentList.forEach((item: any) => {
      if (typeof item.latestDate === 'string') {
        item.latestDate = new Date(item.latestDate + 'Z');
      }
    });
    return response.data;
  },
  async selectComment(
    { dispatch, commit }: any,
    { messageId, studentId }: { messageId: string; studentId: string }
  ) {
    commit('setLoading', true);
    const data = await dispatch('getMessageComment', {
      messageId,
      studentId
    }).catch((error: any) => {
      commit('setLoading', false);
      throw error;
    });
    commit('selectComment', data);
  },
  async getMessageComment(
    { commit }: any,
    { messageId, studentId }: { messageId: string; studentId: string }
  ) {
    const request: MessageRequest.MessageRequest = {
      messageId,
      sendDate: undefined,
      title: undefined,
      note: undefined,
      attachments: undefined,
      returnType: undefined,
      studentIdList: [studentId],
      notificationIdList: undefined,
      messageType: undefined,
      publicType: undefined,
      readType: undefined,
      tutorIdList: undefined,
      dateFrom: undefined,
      dateTo: undefined,
      keyWord: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined
    };
    const response = await Axios.post(
      '/api/Message/CommentView',
      request
    ).catch((error) => {
      throw error;
    });
    if (typeof response.data.sendDate === 'string') {
      response.data.sendDate = new Date(response.data.sendDate + 'Z');
    }
    if (typeof response.data.latestDate === 'string') {
      response.data.latestDate = new Date(response.data.latestDate + 'Z');
    }
    if (typeof response.data.updateDate === 'string') {
      response.data.updateDate = new Date(response.data.updateDate + 'Z');
    }
    response.data.commentList.forEach((item: any) => {
      if (typeof item.sendDate === 'string') {
        item.sendDate = new Date(item.sendDate + 'Z');
      }
    });
    return response.data;
  },
  async sendComment(
    { commit }: any,
    {
      messageId,
      studentId,
      schoolId,
      comment,
      imageList
    }: {
      messageId: string;
      studentId: string;
      schoolId: string;
      comment: string;
      imageList: AttachmentFile[];
    }
  ) {
    let urlList = new Array<AttachmentFileRequest>();
    if (imageList.length === 0) {
      if (!comment || comment === '') {
        throw new Error('コメントを入力してください。');
      }
    } else {
      // ファイルアップロード
      urlList = (await Promise.all(
        imageList.map(async (file) => {
          const formData = new FormData();
          formData.append('file', file.raw as File);
          try {
            const uploadResponse = await Axios.post(
              `/api/File/Upload?keyPath=${schoolId}/Message`,
              formData,
              {
                headers: { 'Content-Type': 'multipart/form-data' }
              }
            );
            return {
              url: uploadResponse.data as string,
              name: file.name
            };
          } catch (error) {
            // TODO:何かしらのエラー処理？
            return {
              url: '',
              name: file.name
            };
          }
        })
      )).filter((file) => file.url !== '');
    }

    const request: MessageRequest.MessageCommentRequest = {
      messageId,
      sendDate: undefined,
      title: undefined,
      note: undefined,
      attachments: urlList,
      returnType: undefined,
      studentIdList: undefined,
      publicType: undefined,
      recieverId: studentId,
      comment: imageList.length === 0 ? comment : '',
      notificationIdList: undefined,
    };
    const response = await Axios.post(
      '/api/Message/CommentSend',
      request
    ).catch((error) => {
      throw new Error('コメントの登録に失敗しました。');
    });
    return response.data;
  },
  async readComment({ commit }: any, commentIdList: string[]) {
    const dataList = await Promise.all(
      commentIdList.map(async (commentId) => {
        const request: MessageRequest.MessageCommentRequest = {
          messageId: commentId,
          sendDate: undefined,
          title: undefined,
          note: undefined,
          attachments: undefined,
          returnType: undefined,
          studentIdList: undefined,
          notificationIdList: undefined,
          publicType: undefined,
          recieverId: undefined,
          comment: undefined,
        };
        const response = await Axios.post(
          '/api/Message/CommentRead',
          request
        ).catch((error) => {
          throw new Error('既読登録に失敗しました。');
        });
        return response.data;
      })
    );
    return dataList;
  },
  async createMessage(
    { commit }: any,
    { message, schoolId }: { message: MessageDetail; schoolId: string }
  ) {
    // ファイルアップロード
    const urlList: AttachmentFileRequest[] = [];
    for (const file of message.attachmentList) {
      if (file.isDelete) {
        continue;
      }

      const formData = new FormData();
      formData.append('file', file.raw as File, file.name);
      try {
        const uploadResponse = await Axios.post(
          `/api/File/Upload?keyPath=${schoolId}/Message`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
        urlList.push({
          url: uploadResponse.data as string,
          name: file.name
        });
      } catch (error) {
        // TODO:何かしらのエラー処理？
      }
    }

    // メッセージ登録
    const request: MessageRequest.MessageDetailRequest = {
      messageId: undefined,
      sendDate: !message.sendDate ? undefined : message.sendDate,
      title: message.title,
      note: message.note,
      attachments: urlList,
      returnType: message.returnType,
      publicType: message.publicType,
      studentIdList: message.studentList.map((student) => student.id),
      notificationIdList: message.notificationIdList,
    };
    const response = await Axios.post('/api/Message/Create', request).catch(
      (error) => {
        throw new Error('メッセージの登録に失敗しました。');
      }
    );

    return response.data;
  },
  async editMessage(
    { commit }: any,
    { message, schoolId }: { message: MessageDetail; schoolId: string }
  ) {
    // ファイルアップロード

    // 既存の有効ファイル
    const urlList = message.attachmentList
      .filter((file) => !file.isDelete && !!file.url)
      .map((file) => ({
        url: file.url as string,
        name: file.name
      }));

    // 新規ファイル
    for (const file of message.attachmentList.filter((fileCheck) => !fileCheck.isDelete && !fileCheck.url)) {
      const formData = new FormData();
      formData.append('file', file.raw as File, file.name);
      try {
        const uploadResponse = await Axios.post(
          `/api/File/Upload?keyPath=${schoolId}/Message`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
        urlList.push({
          url: uploadResponse.data as string,
          name: file.name
        });
      } catch (error) {
        // TODO:何かしらのエラー処理？
      }
    }

    // メッセージ変更
    const request: MessageRequest.MessageDetailRequest = {
      messageId: message.id,
      sendDate: !message.sendDate ? undefined : message.sendDate,
      title: message.title,
      note: message.note,
      attachments: urlList,
      returnType: message.returnType,
      publicType: message.publicType,
      studentIdList: message.studentList.map((student) => student.id),
      notificationIdList: message.notificationIdList,
    };
    const response = await Axios.post('/api/Message/Edit', request).catch(
      (error) => {
        throw new Error('メッセージの変更に失敗しました。');
      }
    );

    commit('changeMessage', message);
    return response.data;
  },
  async deleteMessage({ commit }: any, messageId: string) {
    const request: MessageRequest.MessageDetailRequest = {
      messageId,
      sendDate: undefined,
      title: undefined,
      note: undefined,
      attachments: undefined,
      returnType: undefined,
      studentIdList: undefined,
      notificationIdList: undefined,
      publicType: undefined,
    };
    const response = await Axios.post('/api/Message/Delete', request).catch(
      (error) => {
        throw new Error('メッセージの削除に失敗しました。');
      }
    );
    commit('removeMessage', messageId);
    return response.data;
  },
  async getAttachment({ commit }: any, url: string) {
    const response = await Axios.get(url, { responseType: 'blob' }).catch(
      (error) => {
        throw error;
      }
    );

    return response.data;
  }
};

const mapMutations = {
  setLoading(state: State, loading: boolean) {
    state.loading = loading;
  },
  searchMessages(state: State, data: MessageResponse.MessageDetailList) {
    state.countAll = data.countAll;
    state.messageList = data.messageList;
    state.loading = false;
  },
  selectMessage(state: State, data: MessageResponse.MessageDetail) {
    state.selectedMessage = data;
    state.loading = false;
  },
  selectComment(state: State, data: MessageResponse.MessageComment) {
    state.selectedComment = data;
    state.loading = false;
  },
  changeMessage(state: State, message: MessageDetail) {
    const findIndex = state.messageList.findIndex((value) => {
      return value.id === message.id;
    });
    if (findIndex >= 0) {
      state.messageList.splice(findIndex, 1, message);
    }
  },
  removeMessage(state: State, id: string) {
    const findIndex = state.messageList.findIndex((value) => {
      return value.id === id;
    });
    if (findIndex >= 0) {
      state.messageList.splice(findIndex, 1);
    }
  },
  initSelectedMessage(state: State) {
    state.selectedMessage = {
      id: '',
      tutorId: '',
      tutorUserId: '',
      tutorName: '',
      sendDate: null,
      title: '',
      attachment: 0,
      unReadCount: 0,
      allCount: 0,
      latestDate: null,
      updateDate: new Date(),
      publicType: PublicTypes.Private,
      publicTypeName: '',
      note: '',
      attachmentList: [],
      returnType: ReturnTypes.Disable,
      returnTypeName: '',
      studentList: [],
      notificationIdList: [],
    };
  }
};

export default {
  namespaced: true,
  state: mapState,
  actions: mapActions,
  mutations: mapMutations
};
